// src/pages/SupportTicketsPage.tsx
//
// ★ PHASE 11 — Annex A story 48, "WOG ITSM Helpdesk Ticketing System
// Integration". Any signed-in officer may raise a ticket; PSD manages the
// queue. See `routes/support-tickets.ts`'s own header: this is a LOCAL
// ticket record only — no live WOG ITSM connection exists, and
// `externalTicketRef` is always null.
//
// ★★ WAVE 8 (Task 5) — THREE THINGS THE SCREEN NOW SAYS OUT LOUD, because the
// banner alone left them to be inferred and every one of them is what a
// customer assumes in the opposite direction:
//
//   1. A ticket raised here NOTIFIES NOBODY. Verified rather than asserted:
//      this route calls nothing in `services/administration`'s notification
//      surface, and `050_notification.sql`/`054_notification_delivery.sql`
//      constrain `event_type` to `ECS_ENTITLEMENT_CREATED` plus three bill
//      events — there is no support-ticket event to queue a delivery for.
//   2. PRIORITY ROUTES NOTHING. `priority` is stored by
//      `createSupportTicket` and rendered here; no code anywhere reads it to
//      escalate, page, sort or time anything. `CRITICAL` on a ticket nobody is
//      paged for is an over-claim made of one word.
//   3. THE QUEUE IS WORKED HERE, by PSD, on this screen — which is the answer
//      to "so what happens to my ticket", and a refusal with no answer to that
//      is the same failure wearing a better comment.
import { plainCode } from '../lib/plain-words'
import { useEffect, useState, type FormEvent } from 'react'
import { SgdsButton, SgdsSelect, SgdsSelectOption, SgdsAlert } from '@govtechsg/sgds-web-component/react'
import { api, ApiError } from '../lib/api'
import { useApi } from '../lib/useApi'
import { useOffsetPager } from '../lib/useOffsetPager'
import { Pagination } from '../components/Pagination'
import { EmptyCard, ErrorCard, LoadingCard } from '../components/StateCards'
import { FormResult, type FormResultState } from '../components/FormResult'
import { ConfirmWrite } from '../components/ConfirmWrite'
import { BoundedField } from '../components/BoundedField'
import { boundsProblems } from '../lib/published-bounds'
import { formatIsoDate } from '../lib/format'
import { useSort, sortParams } from '../lib/useSort'
import { SortableTh, SortAnnouncement } from '../components/SortableTh'
import { useAuth } from '../state/AuthContext'
import { StateBadge } from '../components/StatusBadge'

/**
 * ★★★ ADDED 2026-08-24 BY THE SORTABLE-HEADER SWEEP. This table had FOUR plain
 * headers over an offset-paged list — no way to order the queue at all.
 *
 * ⚠ Sorted on the SERVER, and it had to be: `app.support_ticket` grows with use and
 * is never pruned, the route caps a page at `TICKET_PAGE_SIZE`, and a client sort
 * would have reordered the twenty tickets in hand while looking exactly like an
 * answer about the queue.
 *
 * ⚠★ **Priority and Status are ranked at the DATABASE by their meaning, not their
 * alphabet.** `LOW | MEDIUM | HIGH | CRITICAL` sorts `CRITICAL, HIGH, LOW, MEDIUM`
 * as text — and on a support queue that answer decides which ticket somebody picks
 * up next. See `SUPPORT_TICKET_SORT_COLUMNS` and `SortableColumn.rank`.
 */
type TicketSortKey = 'subject' | 'priority' | 'status' | 'createdAt'

const COLUMN_LABELS: Readonly<Record<TicketSortKey, string>> = {
  subject: 'Subject',
  priority: 'Priority',
  status: 'Status',
  createdAt: 'Created',
}


const NEXT_STATUS: Record<string, readonly string[]> = {
  OPEN: ['IN_PROGRESS', 'CLOSED'],
  IN_PROGRESS: ['RESOLVED', 'CLOSED'],
  RESOLVED: ['CLOSED', 'IN_PROGRESS'],
  CLOSED: [],
}

/**
 * ★★ WAVE 8 (Task 3) — ONE copy of each bounded field's label: read by
 * `boundsProblems` for its messages AND by the field that renders it. Two copies
 * drift the day one of them is reworded.
 *
 * ⚠ Only fields the SERVER publishes a bound for appear here. A field the route
 * checks for presence alone is deliberately absent, and its screen therefore says
 * nothing beyond presence — see `routes/administration-field-bounds.ts`.
 */
const BOUNDED_FIELDS = {
  subject: 'Subject',
  description: 'Description',
  systemComponent: 'System component (optional)',
} as const

/** Rows per page — under `MAX_SUPPORT_TICKET_LIMIT` (100), so the server never clamps it. */
const TICKET_PAGE_SIZE = 25

export function SupportTicketsPage() {
  const { user } = useAuth()
  const canManage = user?.scope.role === 'PSD_USER'

  const [subject, setSubject] = useState('')
  const [description, setDescription] = useState('')
  const [priority, setPriority] = useState('MEDIUM')
  const [systemComponent, setSystemComponent] = useState('')
  const [createMessage, setCreateMessage] = useState<string | null>(null)
  /** ★ The queue transition's own outcome — see `handleTransition`, which used to report none. */
  const [transitionState, setTransitionState] = useState<FormResultState>({ status: 'idle' })

  /**
   * ★★ THE TICKET PAGE — 2026-08-18. This table grows with use and is never
   * pruned, and it was capped at 100 with no control able to reach past it;
   * `services/gateway/src/list-reachability.test.ts` is the census that found it.
   *
   * ⚠ A correctness fix landed with it at the repository:
   * `app.support_ticket` was ordered by `created_at DESC` ALONE, which is not
   * a total order, so an OFFSET page 2 could repeat a ticket page 1 showed AND
   * skip one it never did. It now ends `id DESC`.
   */
  const [ticketOffset, setTicketOffset] = useState(0)
  const { sort, onSort, interacted } = useSort<TicketSortKey>()
  const tickets = useApi(
    () => api.supportTickets({ limit: TICKET_PAGE_SIZE, offset: ticketOffset, ...sortParams(sort) }),
    [ticketOffset, sort.column, sort.dir],
  )
  const ticketPager = useOffsetPager(TICKET_PAGE_SIZE, tickets.data?.total)
  useEffect(() => {
    if (ticketPager.offset !== ticketOffset) setTicketOffset(ticketPager.offset)
  }, [ticketPager.offset, ticketOffset])
  /*
    ⚠ A SORT CHANGE RESTARTS THE WALK, and both halves are reset in the same
    handler. Page three of one order is not page three of another — leaving the
    offset alone would land the reader in the middle of a list they have not seen
    the top of. `ReferenceDataPage` and `AccessControlMatrixPage` reset the same
    way, and resetting only the pager would leave this component's fetch offset
    stale for exactly one render, which is one wasted request.
  */
  const onTicketSort = (column: TicketSortKey) => {
    onSort(column)
    ticketPager.reset()
    setTicketOffset(0)
  }
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const detail = useApi(() => (selectedId === null ? Promise.resolve(null) : api.supportTicket(selectedId)), [selectedId])

  /*
    ⚠ The bounds come from the PAYLOAD (`validation` on GET /api/support-tickets),
    never from this file — see `lib/published-bounds.ts`. The list route and the
    create route share one authorisation predicate (`mayRaiseSupportTicket`), so
    an officer who may use this form is an officer who receives its bounds.
  */
  const validation = tickets.data?.validation
  const problems = boundsProblems(
    validation,
    { subject: subject.trim(), description: description.trim(), systemComponent: systemComponent.trim() },
    BOUNDED_FIELDS,
  )

  /**
   * ★★ WAVE 26 — Raise ticket FREEZES a sentence and asks; the write happens only
   * from the confirmation (`ConfirmWrite`'s header says why every irreversible
   * write does this now). A ticket is a permanent row in PSD's queue — nothing
   * here deletes one, and a wrong one is closed by a further event, not taken back.
   * The sentence is composed once, from the fields as they were when Raise ticket
   * was pressed — never re-derived from a form the officer may since have edited.
   */
  const [confirmingCreate, setConfirmingCreate] = useState<string | null>(null)

  const handleCreate = (event: FormEvent) => {
    event.preventDefault()
    if (subject.trim().length === 0 || description.trim().length === 0 || problems.any) return
    const component = systemComponent.trim()
    setConfirmingCreate(
      `Open a support ticket "${subject.trim()}" at ${plainCode(priority)} priority${component.length > 0 ? ` against ${component}` : ''}. It is a local record in PSD's queue on this screen: nothing leaves this deployment, its external reference stays empty, and nobody is notified or paged. There is no undo. A mistake is corrected by another record.`,
    )
  }

  const performCreate = async () => {
    setConfirmingCreate(null)
    try {
      const res = await api.createSupportTicket({
        subject: subject.trim(),
        description: description.trim(),
        priority,
        ...(systemComponent.trim().length > 0 && { systemComponent: systemComponent.trim() }),
      })
      setCreateMessage(res.notice)
      setSubject('')
      setDescription('')
      tickets.reload()
    } catch (error) {
      setCreateMessage(error instanceof Error ? error.message : 'Could not raise the ticket.')
    }
  }

  /**
   * ⚠ **THIS HANDLER HAD NO `try`/`catch`, and the consequence was not a
   * missing message — it was a SILENT FAILURE.**
   *
   * `api.transitionSupportTicket` rejects on any non-2xx. An `async` click
   * handler whose promise rejects with nobody awaiting it produces an
   * unhandled rejection in the console and NOTHING on screen: the officer
   * presses "IN_PROGRESS", the row does not change, and the product has told
   * them nothing at all. A refusal they cannot see is indistinguishable from a
   * button that does not work, and the natural next action is to press it
   * again — against a route that may have partly succeeded.
   *
   * `docs/delivery/DEMO-READINESS-PLAN.md` §Wave 5 lists it by name. The
   * message goes through the same live region as every other outcome on this
   * screen, so a screen-reader user is told too.
   */
  /**
   * ★★ WAVE 26 — a status move asks too. Each one appends an event to the ticket's
   * history that nothing removes; a wrong move is corrected by a further move. The
   * pending request carries the ticket id and the target status FROZEN at the press,
   * alongside the sentence, so the confirm acts on exactly what the sentence names.
   */
  const [confirmingTransition, setConfirmingTransition] = useState<{
    readonly summary: string
    readonly id: string
    readonly to: string
  } | null>(null)

  const requestTransition = (ticket: { readonly id: string; readonly subject: string; readonly status: string }, to: string) => {
    setConfirmingTransition({
      id: ticket.id,
      to,
      summary: `Move ticket "${ticket.subject}" from ${plainCode(ticket.status)} to ${plainCode(to)}. This records an event on the ticket's local history: nothing leaves this deployment and its external reference stays empty. There is no undo. A mistake is corrected by another record.`,
    })
  }

  const handleTransition = async (id: string, to: string) => {
    setConfirmingTransition(null)
    setTransitionState({ status: 'submitting' })
    try {
      await api.transitionSupportTicket(id, to)
      setTransitionState({ status: 'success', message: `Ticket moved to ${to}.` })
      tickets.reload()
      if (selectedId === id) detail.reload()
    } catch (error) {
      setTransitionState({
        status: 'error',
        message:
          error instanceof ApiError
            ? error.message
            : `Could not move this ticket to ${to}. Nothing has changed: try again, or check whether somebody else has already moved it.`,
      })
      // ⚠ RELOADED ON FAILURE TOO. A transition refused because the ticket has
      // already moved on leaves the table showing the OLD status, which is the
      // state that made the officer press the button. Refreshing it is what
      // turns "that did not work" into "here is why".
      tickets.reload()
      if (selectedId === id) detail.reload()
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-3xl mb-2">Support tickets</h1>
        <p className="text-[var(--sgds-body-color-subtle)] max-w-[70ch]">
          Record an issue for PSD to pick up inside this system
          {canManage ? ', or manage the queue below' : ''}.
        </p>
      </div>

      <SgdsAlert show variant="warning" title="No live WOG ITSM connection">
        This is a local ticket record only. Nothing here is sent to, or synchronised with, a real WOG ITSM
        system: every ticket&rsquo;s external reference stays empty.
        {' '}
        <strong>Raising a ticket notifies nobody and pages nobody:</strong> it appears in PSD&rsquo;s queue on
        this screen, and PSD moves it through its statuses here. Priority is recorded for PSD&rsquo;s own
        triage and routes nothing: a CRITICAL ticket reaches no rota and no duty officer. If you need
        somebody now, use the contact route your agency already has.
      </SgdsAlert>

      <form className="pt-card flex flex-col gap-4 max-w-2xl" onSubmit={handleCreate}>
        <h2 className="text-lg">Raise a ticket</h2>
        <BoundedField
          label={BOUNDED_FIELDS.subject}
          value={subject}
          onValueChange={setSubject}
          bounds={validation?.subject}
          problem={problems.of.subject}
          required
        />
        <BoundedField
          label={BOUNDED_FIELDS.description}
          value={description}
          onValueChange={setDescription}
          bounds={validation?.description}
          problem={problems.of.description}
          multiline
          rows={5}
          required
        />
        <SgdsSelect label="Priority" value={priority} onSgdsChange={(e) => setPriority((e.target as HTMLSelectElement).value)}>
          <SgdsSelectOption value="LOW">Low</SgdsSelectOption>
          <SgdsSelectOption value="MEDIUM">Medium</SgdsSelectOption>
          <SgdsSelectOption value="HIGH">High</SgdsSelectOption>
          <SgdsSelectOption value="CRITICAL">Critical</SgdsSelectOption>
        </SgdsSelect>
        <BoundedField
          label={BOUNDED_FIELDS.systemComponent}
          example="e.g. Bills, Entitlement enquiry."
          value={systemComponent}
          onValueChange={setSystemComponent}
          bounds={validation?.systemComponent}
          problem={problems.of.systemComponent}
        />
        <div>
          <SgdsButton
            type="submit"
            variant="primary"
            disabled={subject.trim().length === 0 || description.trim().length === 0 || problems.any || confirmingCreate !== null}
          >
            Raise ticket
          </SgdsButton>
        </div>
        {confirmingCreate !== null && (
          <ConfirmWrite
            summary={confirmingCreate}
            confirmLabel="Yes, open it"
            onConfirm={() => void performCreate()}
            onCancel={() => setConfirmingCreate(null)}
          />
        )}
        {createMessage !== null && (
          /*
            ⚠ `role="status"` and not `alert`: this is the officer's own
            requested outcome, and it carries the notice that no ITSM system
            receives this ticket. See FormResult's own header for why the two
            politeness levels are a decision rather than an attribute.
          */
          <p className="text-sm" role="status" aria-atomic="true">
            {createMessage}
          </p>
        )}
      </form>

      <div>
        <h2 className="text-lg mb-3">{canManage ? 'All tickets' : 'Your tickets'}</h2>
        {tickets.error !== null ? (
          <ErrorCard title="Could not load tickets" message={tickets.error} onRetry={tickets.reload} />
        ) : tickets.initialLoading ? (
          <LoadingCard label="Loading tickets" rows={3} />
        ) : (tickets.data?.tickets.length ?? 0) === 0 ? (
          <EmptyCard title="No tickets" message="Nothing has been raised yet." />
        ) : (
          // ⚠ A fragment, because the pager below is a SIBLING of the table
          // wrapper rather than inside it — a control that scrolled horizontally
          // with a wide table would be unreachable on the narrow viewports
          // P2C1 §3.3(b) requires this app to work on.
          <>
          <SortAnnouncement
            sort={sort}
            interacted={interacted}
            busy={tickets.loading}
            columnLabels={COLUMN_LABELS}
            rowCount={tickets.data?.tickets.length ?? 0}
          />
          <div className="pt-table-wrap">
            <table className="pt-table">
              <caption className="sr-only">
                {canManage ? 'Every support ticket in the queue, with its priority and status' : 'Support tickets you have raised, with their priority and status'}
              </caption>
              <thead>
                <tr>
                  <SortableTh label={COLUMN_LABELS.subject} sortKey="subject" sort={sort} onSort={onTicketSort} />
                  <SortableTh label={COLUMN_LABELS.priority} sortKey="priority" sort={sort} onSort={onTicketSort} />
                  <SortableTh label={COLUMN_LABELS.status} sortKey="status" sort={sort} onSort={onTicketSort} />
                  <SortableTh label={COLUMN_LABELS.createdAt} sortKey="createdAt" sort={sort} onSort={onTicketSort} />
                  {canManage && (
                    <th data-unsortable="one control per row, not data — a sortable header over buttons would claim to order something with no order">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {tickets.data?.tickets.map((ticket) => (
                  <tr key={ticket.id}>
                    <td>
                      <button
                        type="button"
                        className="underline text-left bg-transparent border-0 p-0 cursor-pointer text-[var(--sgds-primary-color-default)]"
                        onClick={() => setSelectedId(ticket.id)}
                      >
                        {ticket.subject}
                      </button>
                    </td>
                    <td>
                      <StateBadge kind="priority" value={ticket.priority} />
                    </td>
                    <td>
                      <StateBadge value={ticket.status} />
                    </td>
                    <td className="pt-tabular">{formatIsoDate(ticket.createdAt.slice(0, 10))}</td>
                    {canManage && (
                      <td className="flex gap-2">
                        {(NEXT_STATUS[ticket.status] ?? []).map((next) => (
                          <SgdsButton
                            key={next}
                            variant="outline"
                            size="sm"
                            type="button"
                            disabled={confirmingTransition !== null || transitionState.status === 'submitting'}
                            onClick={() => requestTransition(ticket, next)}
                          >
                            {next}
                          </SgdsButton>
                        ))}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination
            pager={ticketPager}
            label="tickets"
            total={tickets.data?.total}
            shown={tickets.data?.tickets.length ?? 0}
          />
          {confirmingTransition !== null && (
            <div className="mt-3">
              <ConfirmWrite
                summary={confirmingTransition.summary}
                confirmLabel="Yes, record it"
                onConfirm={() => {
                  const pending = confirmingTransition
                  void handleTransition(pending.id, pending.to)
                }}
                onCancel={() => setConfirmingTransition(null)}
              />
            </div>
          )}
          {/* ★ The queue transition's outcome, in the shared live region — it
              used to report nothing at all on a refusal. */}
          <FormResult state={transitionState} className="px-5 pb-3" />
          </>
        )}
      </div>

      {/*
        ★★★ WAVE 24 — THE THREE STATES, where there used to be one.

        This block read `selectedId !== null && detail.data != null && (…)`. So a
        detail fetch that FAILED, and a detail fetch still in flight, both
        rendered nothing at all: the officer clicked a ticket and the screen did
        not move. `detail.error` was never read anywhere in this file and
        `detail.initialLoading` was never read either — the fetch reported its
        failure faithfully and the page discarded it.

        ⚠ The sibling list twenty lines up has had all three states since it was
        written. The defect was that the DETAIL panel, added later, did not copy
        the pattern — which is why the fix is the same three branches in the same
        order rather than something new.
      */}
      {selectedId !== null && detail.error !== null ? (
        <div className="pt-card">
          <ErrorCard
            title="Could not load that ticket"
            message={detail.error}
            onRetry={detail.reload}
          />
        </div>
      ) : selectedId !== null && detail.initialLoading ? (
        <div className="pt-card">
          <LoadingCard label="Loading the ticket" rows={3} />
        </div>
      ) : null}

      {selectedId !== null && detail.error === null && detail.data !== undefined && detail.data !== null && (
        <div className="pt-card">
          <h2 className="text-lg mb-2">{detail.data.ticket.subject}</h2>
          <p className="mb-4">{detail.data.ticket.description}</p>
          <p className="text-sm text-[var(--sgds-body-color-subtle)] mb-4">
            External ticket reference: {detail.data.ticket.externalTicketRef ?? 'none (no live WOG ITSM connection)'}
          </p>
          <h3 className="font-semibold mb-2">History</h3>
          <ul className="list-disc pl-5">
            {detail.data.events.map((e) => (
              <li key={e.id}>
                {e.fromStatus ?? 'created'} &rarr; {e.toStatus} at {e.occurredAt}
                {e.note !== null ? `: ${e.note}` : ''}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
